import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { getAllProducts, deleteProduct, addProduct, updateProduct, createProductData } from '../firebase/products';
import { deleteCollection, getCollectionStats } from '../firebase/bulkDelete';
import AdminLogin from './AdminLogin';
import MultipleImageUpload from '../components/MultipleImageUpload';
import { testCloudinaryConnection } from '../test-cloudinary';

const Admin = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    description: '',
    imageUrls: [],
    specs: {}
  });
  const [specsFields, setSpecsFields] = useState([
    { key: '', value: '' }
  ]);
  const [selectedCategory, setSelectedCategory] = useState('weapons');
  const [filteredProducts, setFilteredProducts] = useState([]);


  const fetchProducts = useCallback(async () => {
    try {
      const productsData = await getAllProducts();
      setProducts(productsData);
      filterProductsByCategory(productsData, selectedCategory);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMessage(t('products.error'));
    }
  }, [t, selectedCategory]);

  const filterProductsByCategory = (productsList, category) => {
    if (category === 'all') {
      setFilteredProducts(productsList);
    } else {
      const filtered = productsList.filter(product => product.category === category);
      setFilteredProducts(filtered);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    filterProductsByCategory(products, category);
  };

  const handleSeedProducts = async () => {
    try {
      setLoading(true);
      setMessage(t('message.no.sample'));
    } catch (error) {
      console.error('Error seeding products:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllProducts = async () => {
    try {
      setLoading(true);
      setMessage(t('admin.deleting'));
      const deletedCount = await deleteCollection('products');
      setMessage(`${deletedCount} ${t('message.deleted')}`);
      setStats(null);
      setProducts([]);
    } catch (error) {
      console.error('Error deleting products:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStats = async () => {
    try {
      setLoading(true);
      const collectionStats = await getCollectionStats('products');
      setStats(collectionStats);
      setMessage(t('message.success'));
    } catch (error) {
      console.error('Error getting stats:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Temporarily allow all authenticated users for testing
      if (user) {
        console.log('Admin access granted for:', user.email);
        setUser(user);
        fetchProducts();
      } else {
        setUser(null);
      }
      
      // Uncomment below for production with proper admin checks
      /*
      if (user && (user.email === process.env.REACT_APP_ADMIN_EMAIL || 
                   user.email === 'admin@centron.com' ||
                   user.email.endsWith('@centronaerospace.com') ||
                   user.email.endsWith('@centron.com'))) {
        setUser(user);
        fetchProducts();
      } else {
        setUser(null);
      }
      */
    });

    return () => unsubscribe();
  }, [fetchProducts]);

  // Update filtered products when products or selectedCategory changes
  useEffect(() => {
    filterProductsByCategory(products, selectedCategory);
  }, [products, selectedCategory]);

  const handleLoginSuccess = (user) => {
    setUser(user);
    fetchProducts();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setProducts([]);
      setMessage(t('message.success'));
    } catch (error) {
      console.error('Logout error:', error);
      setMessage(t('message.error'));
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      setLoading(true);
      await deleteProduct(productId);
      setMessage(t('message.deleted.single'));
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Validate image upload
      if (!newProduct.imageUrls || newProduct.imageUrls.length === 0) {
        setMessage(t('admin.image.uploadError') + ': ' + t('admin.image.invalidType'));
        return;
      }
      
      // Convert specs fields to object
      const specsObject = {};
      specsFields.forEach(field => {
        if (field.key && field.value) {
          specsObject[field.key] = field.value;
        }
      });
      
      const productData = createProductData(
        newProduct.name,
        selectedCategory, // Use selected category instead of form category
        newProduct.imageUrls,
        specsObject,
        newProduct.description
      );
      await addProduct(productData);
      setMessage(t('message.added'));
      setShowAddForm(false);
      setNewProduct({ name: '', category: '', description: '', imageUrls: [], specs: {} });
      setSpecsFields([{ key: '', value: '' }]);
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpecsChange = (index, field, value) => {
    const newSpecsFields = [...specsFields];
    newSpecsFields[index][field] = value;
    setSpecsFields(newSpecsFields);
  };

  const addSpecsField = () => {
    setSpecsFields([...specsFields, { key: '', value: '' }]);
  };

  const removeSpecsField = (index) => {
    if (specsFields.length > 1) {
      const newSpecsFields = specsFields.filter((_, i) => i !== index);
      setSpecsFields(newSpecsFields);
    }
  };

  const handleImageUpload = (imageUrl, publicId) => {
    setNewProduct(prev => ({
      ...prev,
      imageUrls: [...prev.imageUrls, imageUrl]
    }));
  };

  const handleImageRemove = (index) => {
    setNewProduct(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      category: product.category,
      description: product.description || '',
      imageUrls: product.imageUrls || [],
      specs: product.specs || {}
    });
    
    // Convert specs object to fields array
    const specsArray = Object.entries(product.specs || {}).map(([key, value]) => ({ key, value }));
    setSpecsFields(specsArray.length > 0 ? specsArray : [{ key: '', value: '' }]);
    
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Validate image upload
      if (!newProduct.imageUrls || newProduct.imageUrls.length === 0) {
        setMessage(t('admin.image.uploadError') + ': ' + t('admin.image.invalidType'));
        return;
      }
      
      // Convert specs fields to object
      const specsObject = {};
      specsFields.forEach(field => {
        if (field.key && field.value) {
          specsObject[field.key] = field.value;
        }
      });
      
      const productData = createProductData(
        newProduct.name,
        newProduct.category,
        newProduct.imageUrls,
        specsObject,
        newProduct.description
      );
      
      // Update the product
      await updateProduct(editingProduct.id, productData);
      setMessage(t('message.updated'));
      setShowEditForm(false);
      setEditingProduct(null);
      setNewProduct({ name: '', category: '', description: '', imageUrls: [], specs: {} });
      setSpecsFields([{ key: '', value: '' }]);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      setMessage(t('message.error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingProduct(null);
    setNewProduct({ name: '', category: '', description: '', imageUrls: [], specs: {} });
    setSpecsFields([{ key: '', value: '' }]);
  };

  // handleRemoveAllImages function removed as it's not used

  const handleTestCloudinary = async () => {
    try {
      setLoading(true);
      const result = await testCloudinaryConnection();
      if (result.success) {
        setMessage('Cloudinary connection successful! ✅');
      } else {
        setMessage('Cloudinary connection failed: ' + result.error);
      }
    } catch (error) {
      setMessage('Test failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // If not logged in, show login form
  if (!user) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50 admin-panel">
      <div className="container-custom py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-dark-grey">{t('admin.title')}</h1>
              <p className="text-gray-600 mt-2">{t('admin.welcome')}, {user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              {t('admin.logout')}
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Database Management */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-dark-grey mb-6">{t('admin.database.title')}</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-dark-grey mb-3">{t('admin.sample.title')}</h3>
                  <p className="text-gray-600 mb-4">
                    {t('admin.sample.description')}
                  </p>
                  <button
                    onClick={handleSeedProducts}
                    disabled={loading}
                    className="bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold cursor-not-allowed"
                  >
                    {t('admin.sample.button')}
                  </button>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-dark-grey mb-3">{t('admin.stats.title')}</h3>
                  <p className="text-gray-600 mb-4">
                    {t('admin.stats.description')}
                  </p>
                  <button
                    onClick={handleGetStats}
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('admin.loading') : t('admin.stats.button')}
                  </button>
                  
                  {stats && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Collection Stats:</h4>
                      <p className="text-blue-700">Collection: <strong>{stats.collectionName}</strong></p>
                      <p className="text-blue-700">Total Documents: <strong>{stats.totalDocuments}</strong></p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-dark-grey mb-3">Test Cloudinary</h3>
                  <p className="text-gray-600 mb-4">
                    Test Cloudinary connection and configuration
                  </p>
                  <button
                    onClick={handleTestCloudinary}
                    disabled={loading}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Testing...' : 'Test Cloudinary Connection'}
                  </button>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-dark-grey mb-3">{t('admin.delete.title')}</h3>
                  <p className="text-gray-600 mb-4">
                    ⚠️ <strong>Warning:</strong> {t('admin.delete.description')}
                  </p>
                  <button
                    onClick={handleDeleteAllProducts}
                    disabled={loading}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('admin.deleting') : t('admin.delete.button')}
                  </button>
                </div>

                {message && (
                  <div className={`p-4 rounded-lg ${
                    message.includes('Error') 
                      ? 'bg-red-100 text-red-700 border border-red-200' 
                      : 'bg-green-100 text-green-700 border border-green-200'
                  }`}>
                    {message}
                  </div>
                )}
              </div>
            </div>

            {/* Product Management */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-dark-grey">WEAPONS & AMMUNITION MANAGEMENT</h2>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  {showAddForm ? t('admin.add.cancel') : 'ADD PRODUCT'}
                </button>
              </div>

              {/* Category Tabs */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => handleCategoryChange('weapons')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-200 ${
                    selectedCategory === 'weapons'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  WEAPONS
                </button>
                <button
                  onClick={() => handleCategoryChange('ammunition')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-200 ${
                    selectedCategory === 'ammunition'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  AMMUNITION
                </button>
                <button
                  onClick={() => handleCategoryChange('all')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-200 ${
                    selectedCategory === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ALL PRODUCTS
                </button>
              </div>

              {/* Add Product Form */}
              {showAddForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 border border-gray-200 rounded-lg"
                >
                                  <form onSubmit={handleAddProduct} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Product Name"
                      value={newProduct.name}
                      onChange={handleInputChange}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                      required
                    />
                    <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
                      <span className="text-gray-600 font-semibold">
                        Category: {selectedCategory.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Product Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('admin.form.description')}
                    </label>
                    <textarea
                      name="description"
                      placeholder={t('admin.form.description.placeholder')}
                      value={newProduct.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow resize-vertical"
                    />
                  </div>
                  
                  {/* Multiple Image Upload Component */}
                  <MultipleImageUpload
                    onImageUpload={handleImageUpload}
                    onImageRemove={handleImageRemove}
                    currentImages={newProduct.imageUrls.map(url => ({ url }))}
                  />
                  
                  {/* Dynamic Specs Fields */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-dark-grey">{t('admin.form.specs.title')}</h4>
                      <button
                        type="button"
                        onClick={addSpecsField}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors duration-200"
                      >
                        {t('admin.form.specs.add')}
                      </button>
                    </div>
                    
                    {specsFields.map((field, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          placeholder={t('admin.form.specs.name')}
                          value={field.key}
                          onChange={(e) => handleSpecsChange(index, 'key', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                        />
                        <input
                          type="text"
                          placeholder={t('admin.form.specs.value')}
                          value={field.value}
                          onChange={(e) => handleSpecsChange(index, 'value', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                        />
                        {specsFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSpecsField(index)}
                            className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 transition-colors duration-200"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-accent-yellow text-dark-grey py-2 px-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-200 disabled:opacity-50"
                  >
                    {loading ? t('admin.form.adding') : t('admin.form.submit')}
                  </button>
                </form>
                </motion.div>
              )}

              {/* Edit Product Form */}
              {showEditForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-dark-grey">Edit Product: {editingProduct?.name}</h3>
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleUpdateProduct} className="space-y-4">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Product Name"
                      value={newProduct.name}
                      onChange={handleInputChange}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                      required
                    />
                    <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
                      <span className="text-gray-600 font-semibold">
                        Category: {editingProduct?.category?.toUpperCase() || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Product Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('admin.form.description')}
                    </label>
                    <textarea
                      name="description"
                      placeholder={t('admin.form.description.placeholder')}
                      value={newProduct.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow resize-vertical"
                    />
                  </div>
                    
                    {/* Multiple Image Upload Component */}
                    <MultipleImageUpload
                      onImageUpload={handleImageUpload}
                      onImageRemove={handleImageRemove}
                      currentImages={newProduct.imageUrls.map(url => ({ url }))}
                    />
                    
                    {/* Dynamic Specs Fields */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-dark-grey">{t('admin.form.specs.title')}</h4>
                        <button
                          type="button"
                          onClick={addSpecsField}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors duration-200"
                        >
                          {t('admin.form.specs.add')}
                        </button>
                      </div>
                      
                      {specsFields.map((field, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            placeholder={t('admin.form.specs.name')}
                            value={field.key}
                            onChange={(e) => handleSpecsChange(index, 'key', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                          />
                          <input
                            type="text"
                            placeholder={t('admin.form.specs.value')}
                            value={field.value}
                            onChange={(e) => handleSpecsChange(index, 'value', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-yellow"
                          />
                          {specsFields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSpecsField(index)}
                              className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 transition-colors duration-200"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        {loading ? 'Updating...' : 'Update Product'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-600 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* Products List */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-dark-grey">
                  {selectedCategory === 'all' ? 'ALL PRODUCTS' : selectedCategory.toUpperCase()} 
                  ({filteredProducts.length})
                </h3>
                {filteredProducts.map((product) => (
                  <div key={product.id} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-semibold text-dark-grey">{product.name}</h4>
                      <p className="text-sm text-gray-600">{product.category}</p>
                      {product.description && (
                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.keys(product.specs || {}).length} {t('admin.products.specs')}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        disabled={loading}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50"
                      >
                        {t('admin.products.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        disabled={loading}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-colors duration-200 disabled:opacity-50"
                      >
                        {t('admin.products.delete')}
                      </button>
                    </div>
                  </div>
                ))}
                {filteredProducts.length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    {selectedCategory === 'all' 
                      ? 'No products found in database.' 
                      : `No ${selectedCategory} found. Add some products to this category.`
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin; 